import { Component, computed, inject, Input, OnInit, ViewChild } from '@angular/core';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TagModule } from 'primeng/tag';
import { MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { DynamicForm, SelectOption } from '../../../components/form-builder/form-builder.types';
import { DrawerModule } from 'primeng/drawer';
import { AddressService } from './address.service';
import { RefParamsService } from '../ref-params/ref-params.service';
import { Address, RefTypeEnum } from '@/types';
import { DataTableRowActions, DynamicDataTable } from '../../../components/data-table-builder/data-table.types';
import { getAddressDataTableStructure } from './utils/address-data-table-object';
import { DrawerComponent } from '../../../components/drawer/drawer.component';
import { DrawerObject } from '../../../components/drawer/drawer.types';
import { DataTableBuilderComponent } from '../../../components/data-table-builder/data-table-builder.component';
import { DialogObject } from '../../../components/dialog/dialog.types';
import { DialogComponent } from '../../../components/dialog/dialog.component';
import { CompanyService } from '../company/company.service';
import { MessageService } from 'primeng/api';
import { getFormBuilderObjectFactory } from '../../../components/form-builder/utils/form-builder-factory';
import { AddressRepository } from '../../../store/address-state/address-state.repository';
import { AddressDto } from '@/types/dtos.types';
import { getAddressAddDrawerObject } from './utils/address-add-drawer-object';
import { getAddressFormObject } from './utils/address-form-object';
import { getAddressUpdateDrawerObject } from './utils/address-update-drawer-object';
import { getAddressDeleteObject } from './utils/address-delete-dialog-object';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'sm-address',
  imports: [
    PageHeaderComponent,
    CommonModule,
    ButtonModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    TagModule,
    MenuModule,
    InputTextModule,
    FormsModule,
    SelectModule,
    DrawerModule,
    DrawerComponent,
    DataTableBuilderComponent,
    DialogComponent,
  ],
  templateUrl: './address.component.html',
  styleUrl: './address.component.scss',
})
export class AddressComponent implements OnInit {
  // services
  refParamsService = inject(RefParamsService);
  addressService = inject(AddressService);
  addressRepository = inject(AddressRepository);
  companyService = inject(CompanyService);
  messageService = inject(MessageService);
  translateService = inject(TranslateService);

  //inputs
  @Input() companyId?: number;
  companyContext = computed(() => !!this.companyId);

  // breadcrumb
  breadcrumb = [
    {
      label: 'Paramètres',
      routerLink: ['/settings'],
    },
    {
      label: 'Adresse',
      routerLink: ['/settings', 'address'],
    },
  ];

  // drawer
  @ViewChild('drawer') drawerComponent!: DrawerComponent;
  addressDrawer?: DrawerObject;

  //dialog
  @ViewChild('dialog') dialogComponent!: DialogComponent;
  addressDialog?: DialogObject;

  //form-object
  formObject?: DynamicForm = getFormBuilderObjectFactory();
  addressTypesOptions: SelectOption[] = [];

  //data-table
  //row-actions
  rowActions?: DataTableRowActions;
  //data
  data: Address[] = [];
  //data-table-object
  dataTableObject?: DynamicDataTable;
  loading = false;

  ngOnInit(): void {
    this.loadData();
    this.refParamsService.getSpecializedRefParams(RefTypeEnum.ADDRESS_TYPE).subscribe(data => {
      this.addressTypesOptions = data.map(refParams => {
        return { name: refParams.description, code: refParams.id };
      });
    });
    this.rowActions = {
      editAction: () => {
        this.formObject = getAddressFormObject({
          store: this.addressRepository,
          addressTypesOptions: this.addressTypesOptions,
        });
        this.addressDrawer = getAddressUpdateDrawerObject({
          store: this.addressRepository,
          formObject: this.formObject,
          action: () =>
            this.updateAddress(this.addressRepository.getNested('response', 'id'), {
              ...this.addressRepository.getNested('dto'),
              societeId: this.companyId!,
            }),
          toggleVisibility: () => this.drawerComponent.toggleVisible(),
        });
        this.drawerComponent.toggleVisible();
      },
      deleteAction: () => {
        this.addressDialog = getAddressDeleteObject({
          t: this.translateService,
          store: this.addressRepository,
          representation: this.addressRepository.getNested('response', 'id'),
          action: () => {
            this.deleteAddress(this.addressRepository.getNested('response', 'id'));
          },
          toggleVisiblity: () => this.dialogComponent.toggleVisible(),
        });
        this.dialogComponent.toggleVisible();
      },
    };
    this.dataTableObject = getAddressDataTableStructure({
      store: this.addressRepository,
      createAction: () => {
        this.formObject = getAddressFormObject({
          store: this.addressRepository,
          addressTypesOptions: this.addressTypesOptions,
        });
        this.addressDrawer = getAddressAddDrawerObject({
          store: this.addressRepository,
          formObject: this.formObject,
          action: () => this.addAddress(this.addressRepository.getNested('dto')),
          toggleVisibility: () => this.drawerComponent.toggleVisible(),
        });
        this.drawerComponent.toggleVisible();
      },
      rowActions: this.rowActions,
    });
  }

  loadData() {
    this.loading = true;
    const error = () => {
      this.loading = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Une erreur est survenue lors de la récupération des adresses',
      });
    };
    if (this.companyId) {
      this.companyService.getCompanyAddresses(this.companyId).subscribe({
        next: data => {
          this.data = data.content;
          this.loading = false;
        },
        error,
      });
    } else {
      this.addressService.getAddresses().subscribe({
        next: data => {
          this.data = data.content;
          this.loading = false;
        },
        error,
      });
    }
  }

  addAddress(address: AddressDto) {
    const next = () => {
      this.drawerComponent.toggleVisible();
      this.loadData();
      this.loading = false;
      this.addressRepository.reset();
      this.messageService.add({
        severity: 'success',
        summary: 'Adresse ajouté',
        detail: "L'adresse a été ajouté avec succès.",
      });
    };

    const error = () => {
      this.loading = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: "Une erreur est survenue lors de l'ajout de l'adresse.",
      });
    };

    if (this.companyId) {
      this.companyService.addCompanyAddress(this.companyId, address).subscribe({
        next,
        error,
      });
    } else {
      this.addressService.addAddress(address).subscribe({
        next,
        error,
      });
    }
  }

  updateAddress(id: number, address: AddressDto) {
    this.addressService.updateAddress(id, address).subscribe({
      next: () => {
        this.drawerComponent.toggleVisible();
        this.loadData();
        this.loading = false;
        this.addressRepository.reset();
        this.messageService.add({
          severity: 'success',
          summary: 'Addresse modifié',
          detail: "L'adresse a été modifié avec succès.",
        });
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: "Une erreur est survenue lors de la modification de l'adresse.",
        });
      },
    });
  }

  deleteAddress(id: number) {
    this.addressService.deleteAddress(id).subscribe({
      next: () => {
        this.dialogComponent.toggleVisible();
        this.loadData();
        this.loading = false;
        this.addressRepository.reset();
        this.messageService.add({
          severity: 'success',
          summary: 'Addresse supprimé',
          detail: "L'adresse a été supprimé avec succès.",
        });
      },
      error: () => {
        this.loading = false;
        this.addressRepository.reset();
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: "Une erreur est survenue lors de la suppression de l'adresse.",
        });
      },
    });
  }
}
